/**
 * PlayerCard - Individual player card with minutes stepper and projections
 *
 * Features:
 * - Player avatar, name, position, team
 * - Minutes stepper with +/- controls
 * - Stat projections (PTS, REB, AST, 3PM) with % change indicators
 * - "Edit Rates" button to open rate editing dialog
 * - Expandable stats table with all props
 */
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Paper,
} from 'omaha-design-system';
import { CaretUp, CaretDown, ArrowClockwise } from '@phosphor-icons/react';
import { PlayerData, DisplayGame } from '../../data/types';
import { setPlayerCustomization, resetPlayerCustomization, updateBetSlipProjections } from '../../store/slice';
import { useCustomProjections, getBaselineRates, computeCustomProjections, CustomProjectionValues } from '../../hooks/useCustomProjections';
import { PlayerVolumes, PlayerCustomization } from '../../store/types';
import { useDebouncedEVCalculation } from '../../hooks/useDebouncedEVCalculation';
import { buildEVRequest } from '../../helpers/buildEVRequest';
import { CalculateEVResponse } from '../../data/evTypes';
import { IRootState } from '../../../../store/reducer';
import EditRatesDialog from './EditRatesDialog';
import BattingOrderBadge from '../BattingOrderBadge';
import StatsTable from './StatsTable';
import { getStatDecimals } from '../../data/statLabels';
import { leagueUsesDynamicStats } from '../../data/leagueConfig';

interface PlayerCardProps {
  player: PlayerData;
  game: DisplayGame;
  isExpanded: boolean;
  onToggleExpand: (playerId: string) => void;
  /** Pre-computed mobile breakpoint from parent - avoids useMediaQuery per card */
  isMobile: boolean;
}

// Format EV percentage with sign, color, and "EV" label
const formatEV = (evPct: number | null): { text: string; color: string } => {
  if (evPct === null || Math.abs(evPct) < 0.1) {
    return { text: '', color: 'text.secondary' };
  }
  const sign = evPct > 0 ? '+' : '';
  const text = `${sign}${evPct.toFixed(1)}% EV`;
  const color = evPct > 0 ? '#66BB6A' : evPct < 0 ? '#EF5350' : 'text.secondary';
  return { text, color };
};

// Get injury status display text and color
const getInjuryDisplay = (
  status: string | undefined
): { text: string; color: string; bgColor: string } | null => {
  if (!status) return null;

  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'out') {
    return { text: 'OUT', color: '#F2F2F3', bgColor: '#EF5350' };
  }
  if (normalizedStatus === 'doubtful') {
    return { text: 'DTD', color: '#F2F2F3', bgColor: '#FF9800' };
  }
  if (normalizedStatus === 'questionable' || normalizedStatus === 'gtd') {
    return { text: 'GTD', color: 'rgba(0, 0, 0, 0.87)', bgColor: '#FFEB3B' };
  }
  if (normalizedStatus === 'probable') {
    return { text: 'PROB', color: 'rgba(0, 0, 0, 0.87)', bgColor: '#66BB6A' };
  }

  return null;
};

// Stat name mapping from display label to props stat name
const statNameMap: Record<string, string[]> = {
  // NBA stats
  pts: ['Points', 'PTS'],
  reb: ['Rebounds', 'REB'],
  ast: ['Assists', 'AST'],
  threes: ['3-Pointers', '3PM', 'Threes'],
  // NHL stats
  goals: ['Total Player Goals', 'Goals', 'Player Goals', 'G'],
  nhlAssists: ['Total Assists', 'Assists', 'AST'],
  sog: ['Total Shots on Goal', 'Shots on Goal', 'SOG'],
  blocks: ['Blocked Shots', 'BLK'],
  // MLB stats (key matches normalized stat from transformPropsData)
  h: ['H', 'Hits', 'Total Hits'],
  hr: ['HR', 'Home Runs', 'Total Home Runs'],
  r: ['R', 'Runs Scored', 'Total Player Runs'],
  rbi: ['RBI', 'Runs Batted In', 'Total RBI', 'Total RBIs'],
  tb: ['TB', 'Total Bases'],
  so: ['SO', 'Batter Strikeouts', 'Total Batter Strikeouts'],
  sb: ['SB', 'Stolen Bases', 'Total Stolen Bases'],
  bb: ['BB', 'Total Walks', 'Total Batter Walks'],
  k: ['K', 'Pitcher Strikeouts', 'Total Strikeouts', 'Strikeouts Pitched'],
  er: ['ER', 'Earned Runs', 'Earned Runs Allowed', 'Total Earned Runs'],
  ip: ['IP', 'Innings Pitched'],
  ha: ['HA', 'Hits Allowed', 'Pitcher Hits Allowed', 'Total Hits Allowed'],
  // NFL stats (keys match CustomProjectionValues; names match normalized stat codes)
  passAtt: ['Pass Att'],
  rushAtt: ['Rush Att'],
  receptions: ['Rec'],
  passYds: ['Pass Yds'],
  passTds: ['Pass TDs'],
  comp: ['Comp'],
  ints: ['INT'],
  rushYds: ['Rush Yds'],
  rushTds: ['Rush TDs'],
  recYds: ['Rec Yds'],
  recTds: ['Rec TDs'],
  passRushYds: ['Pass+Rush Yds'],
  rushRecYds: ['Rush+Rec Yds'],
  anyTd: ['Any TD'],
  rushRecTds: ['Rush+Rec TDs'],
};

// NFL volume lever per position group (the football equivalent of minutes)
type NflGroup = 'QB' | 'RB' | 'REC';
const NFL_LEVER: Record<NflGroup, { key: keyof PlayerVolumes; label: string; max: number; step: number }> = {
  QB: { key: 'passAtt', label: 'PASS ATT', max: 80, step: 1 },
  RB: { key: 'rushAtt', label: 'RUSH ATT', max: 50, step: 1 },
  REC: { key: 'targets', label: 'TGT', max: 25, step: 0.5 },
};

// League-specific stat configurations
interface StatConfig {
  key: string;
  label: string;
  projectionKey?: keyof typeof projectionKeyMap;
}

const projectionKeyMap = {
  pts: 'pts',
  reb: 'reb',
  ast: 'ast',
  threes: 'threes',
} as const;

const NBA_STATS: StatConfig[] = [
  { key: 'pts', label: 'PTS', projectionKey: 'pts' },
  { key: 'reb', label: 'REB', projectionKey: 'reb' },
  { key: 'ast', label: 'AST', projectionKey: 'ast' },
  { key: 'threes', label: '3PM', projectionKey: 'threes' },
];

const NHL_STATS: StatConfig[] = [
  { key: 'goals', label: 'G' },
  { key: 'nhlAssists', label: 'A' },
  { key: 'sog', label: 'SOG' },
  { key: 'blocks', label: 'BLK' },
];

// Get the best EV for a stat from player props (original values)
// Looks at BOTH Over and Under props and returns the highest EV
const getOriginalBestEV = (player: PlayerData, statKey: string): number | null => {
  const statNames = statNameMap[statKey] || [];

  // Find ALL matching props (both Over and Under)
  // Try statNameMap first, then fall back to direct match on the stat label (for MLB dynamic stats)
  const matchingProps = player.props?.filter((p) =>
    statNames.some((name) => p.stat.toLowerCase() === name.toLowerCase()) ||
    p.stat.toLowerCase() === statKey.toLowerCase()
  ) || [];

  if (matchingProps.length === 0) {
    return null;
  }

  // Find the best EV among all sportsbooks across all matching props (Over and Under)
  let bestEV = -Infinity;
  for (const prop of matchingProps) {
    for (const sb of prop.sportsbooks || []) {
      const ev = sb.evPct ?? -Infinity;
      if (ev > bestEV) {
        bestEV = ev;
      }
    }
  }

  return bestEV === -Infinity ? null : bestEV;
};

// Get the best custom EV for a stat from custom EV data
// Looks at BOTH Over and Under outcomes and returns the highest EV
const getCustomBestEV = (
  customEVData: CalculateEVResponse | null,
  statKey: string
): number | null => {
  if (!customEVData?.outcomes?.length) {
    return null;
  }

  const statNames = statNameMap[statKey] || [];

  // Find ALL matching outcomes (both Over and Under)
  const matchingOutcomes = customEVData.outcomes.filter((o) =>
    statNames.some((name) => o.statType.toLowerCase() === name.toLowerCase())
  );

  if (matchingOutcomes.length === 0) {
    return null;
  }

  // Find the best blended EV among all sportsbooks across all matching outcomes (Over and Under)
  let bestEV = -Infinity;
  for (const outcome of matchingOutcomes) {
    for (const sb of outcome.sportsbooks || []) {
      const ev = sb.blendedEV != null ? sb.blendedEV * 100 : -Infinity;
      if (ev > bestEV) {
        bestEV = ev;
      }
    }
  }

  return bestEV === -Infinity ? null : bestEV;
};

// Get best EV, preferring custom EV data when available
const getBestEV = (
  player: PlayerData,
  statKey: string,
  customEVData: CalculateEVResponse | null,
  isCustom: boolean
): number | null => {
  if (isCustom && customEVData) {
    const customEV = getCustomBestEV(customEVData, statKey);
    if (customEV !== null) {
      return customEV;
    }
  }
  return getOriginalBestEV(player, statKey);
};

// Get the best line (projection) for a stat from player props
// Used for NHL stats where we don't have separate projections
const getBestLineForStat = (player: PlayerData, statKey: string): number | null => {
  const statNames = statNameMap[statKey] || [];

  // Find matching props
  const matchingProps = player.props?.filter((p) =>
    statNames.some((name) =>
      p.stat.toLowerCase() === name.toLowerCase() ||
      p.originalStat?.toLowerCase() === name.toLowerCase()
    )
  ) || [];

  if (matchingProps.length === 0) {
    return null;
  }

  // Return the line from the first matching prop (they should all have the same line)
  const firstProp = matchingProps[0];
  if (firstProp.sportsbooks && firstProp.sportsbooks.length > 0) {
    return firstProp.sportsbooks[0].line;
  }

  return firstProp.projection;
};

/**
 * Projection for a stat from a computed projection set. Shared by the effect that re-prices after a
 * customization lands in Redux and by the immediate re-price the edit dialog triggers, so both see the
 * same numbers.
 */
export function projectionForStat(projections: CustomProjectionValues, league: string, stat: string): number {
  const normalizedStat = stat.toLowerCase().replace(/[\s\-_]/g, '');

  if (league === 'NHL') {
    // NHL stats - check these first to avoid collision with NBA codes
    if (normalizedStat.includes('goal') || normalizedStat === 'g') {
      return projections.goals;
    }
    // "Total Assists" normalizes to "AST" — must match 'ast' here, not NBA assists
    if (normalizedStat.includes('assist') || normalizedStat === 'ast') {
      return projections.nhlAssists;
    }
    if (normalizedStat.includes('shot') || normalizedStat === 'sog') {
      return projections.sog;
    }
    if (normalizedStat.includes('save') || normalizedStat === 'sv') {
      return projections.saves;
    }
    // "Total Points" normalizes to "PTS" — must match 'pts' here, not NBA points
    if (normalizedStat.includes('point') || normalizedStat === 'pts' || normalizedStat === 'nhlpoints') {
      return projections.nhlPoints;
    }
    if (normalizedStat.includes('block') || normalizedStat === 'blk') {
      return projections.blocks;
    }
    return 0;
  }

  if (league === 'NFL') {
    const nfl: Record<string, number> = {
      passatt: projections.passAtt,
      passyds: projections.passYds,
      passtds: projections.passTds,
      comp: projections.comp,
      int: projections.ints,
      rushatt: projections.rushAtt,
      rushyds: projections.rushYds,
      rushtds: projections.rushTds,
      rec: projections.receptions,
      recyds: projections.recYds,
      rectds: projections.recTds,
      'pass+rushyds': projections.passRushYds,
      'rush+recyds': projections.rushRecYds,
      anytd: projections.anyTd,
      'rush+rectds': projections.rushRecTds,
    };
    return nfl[normalizedStat] ?? 0;
  }

  // NBA Single stats
  if (normalizedStat.includes('point') || normalizedStat === 'pts') {
    return projections.pts;
  }
  if (normalizedStat.includes('rebound') || normalizedStat === 'reb') {
    return projections.reb;
  }
  if (normalizedStat.includes('assist') || normalizedStat === 'ast') {
    return projections.ast;
  }
  if (normalizedStat.includes('3') || normalizedStat.includes('three')) {
    return projections.threes;
  }

  // NBA Combo stats - PRA (Points + Rebounds + Assists)
  if (normalizedStat.includes('pts_rebs_asts') || normalizedStat === 'pra') {
    return projections.pts + projections.reb + projections.ast;
  }
  // P+R (Points + Rebounds)
  if (normalizedStat === 'p+r' || normalizedStat === 'pr') {
    return projections.pts + projections.reb;
  }
  // P+A (Points + Assists)
  if (normalizedStat === 'p+a' || normalizedStat === 'pa') {
    return projections.pts + projections.ast;
  }
  // R+A (Rebounds + Assists)
  if (normalizedStat === 'r+a' || normalizedStat === 'ra') {
    return projections.reb + projections.ast;
  }

  return 0;
}

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  game,
  isExpanded,
  onToggleExpand,
  isMobile,
}) => {
  const dispatch = useDispatch();
  const [editRatesOpen, setEditRatesOpen] = useState(false);

  // Stable callback for toggle expand - avoids creating new function on each render
  const handleCardClick = useCallback(() => {
    onToggleExpand(player.id);
  }, [onToggleExpand, player.id]);

  const { minutes, projections, changes, baseline, rates, volumes, hasCustomizations } =
    useCustomProjections(player, game.id, game.startTime);
  // Value key over every projection (NBA, NHL and NFL), so a change to any of them re-prices custom EVs
  const projectionsKey = JSON.stringify(projections);

  // Get filter state for stokWeight/marketWeight
  const filters = useSelector((state: IRootState) => state.stokasticProps.filters);

  // NFL: which volume lever this player gets (none → read-only, e.g. kickers / TD-only backups)
  const isNFL = filters.league === 'NFL';
  // Position decides the lever (a WR with a posted rushing-attempts line is still a receiver); the feed's
  // volumes are the fallback when the position is missing.
  const nflGroupFromPosition: NflGroup | null = (() => {
    const pos = player.position?.toUpperCase();
    if (pos === 'QB') return 'QB';
    if (pos === 'RB' || pos === 'FB') return 'RB';
    if (pos === 'WR' || pos === 'TE') return 'REC';
    return null;
  })();
  const nflGroupFromVolumes: NflGroup | null =
    baseline.volumes.passAtt > 0 ? 'QB' : baseline.volumes.rushAtt > 0 ? 'RB' : baseline.volumes.targets > 0 ? 'REC' : null;
  const nflGroup: NflGroup | null = !isNFL ? null : nflGroupFromPosition ?? nflGroupFromVolumes;
  const nflLever = nflGroup ? NFL_LEVER[nflGroup] : null;
  // The card's stepper lever: minutes for NBA/NHL, the primary volume for NFL
  const leverKey: string = nflLever ? nflLever.key : 'mins';
  const leverLabel = nflLever ? nflLever.label : 'MINS';
  const leverValue = nflLever ? volumes[nflLever.key] : minutes;
  const leverBaseline = nflLever ? baseline.volumes[nflLever.key] : baseline.minutes;
  const leverMax = nflLever ? nflLever.max : 48;
  const leverStep = nflLever ? nflLever.step : 1;

  // EV calculation hook
  const {
    triggerCalculation,
    cancel: cancelEVCalculation,
    data: customEVData,
    isLoading: isEVLoading,
    isCustom: hasCustomEV,
  } = useDebouncedEVCalculation(player.id, game.id);

  // Get projection value for a given stat name (used by buildEVRequest)
  // Must be league-aware because transformPropsData normalizes NHL "Total Assists" to "AST"
  // and "Total Points" to "PTS", which collide with NBA stat codes.
  const getProjectionForStat = useCallback(
    (stat: string): number => projectionForStat(projections, filters.league, stat),
    [projections, filters.league]
  );

  // Trigger EV calculation for all stats affected by minutes change
  const triggerEVForAllStats = useCallback(() => {
    // Use league-specific stats
    const statsToRecalculate = filters.league === 'NBA'
      ? ['points', 'rebounds', 'assists', 'threes']
      : filters.league === 'NHL'
        ? ['goals', 'nhlassists', 'sog', 'saves', 'nhlpoints', 'blocks']
        : filters.league === 'NFL'
          ? ['passatt', 'passyds', 'passtds', 'comp', 'int', 'rushatt', 'rushyds', 'rushtds', 'rec', 'targets', 'recyds', 'rectds', 'pass+rushyds', 'rush+recyds', 'anytd', 'rush+rectds']
          : [];

    const request = buildEVRequest(
      player,
      statsToRecalculate,
      getProjectionForStat,
      filters.stokWeight,
      filters.marketWeight,
      filters.league
    );
    if (request.outcomes.length > 0) {
      triggerCalculation(request);
    }
  }, [player, getProjectionForStat, filters.stokWeight, filters.marketWeight, filters.league, triggerCalculation]);

  // Trigger EV calculation for specific stats (used by EditRatesDialog)
  const triggerEVForStats = useCallback(
    (changedStats: string[], appliedCustomization?: PlayerCustomization) => {
      // The dialog calls this right after dispatching; Redux hasn't re-rendered us yet, so compute the
      // projections from the customization it just applied instead of the ones in this closure.
      const source = appliedCustomization
        ? computeCustomProjections(player, appliedCustomization).projections
        : projections;
      const request = buildEVRequest(
        player,
        changedStats,
        (stat) => projectionForStat(source, filters.league, stat),
        filters.stokWeight,
        filters.marketWeight,
        filters.league
      );
      if (request.outcomes.length > 0) {
        triggerCalculation(request);
      }
    },
    [player, projections, filters.stokWeight, filters.marketWeight, filters.league, triggerCalculation]
  );

  // Local string state for the desktop lever input (minutes, or NFL volume) to allow empty values during editing
  const [minutesInputValue, setMinutesInputValue] = useState(leverValue.toFixed(1));

  // Sync input value when the external lever value changes (e.g., from stepper buttons or reset)
  useEffect(() => {
    setMinutesInputValue(leverValue.toFixed(1));
  }, [leverValue]);

  // Apply a new lever value: minutes for NBA/NHL, the primary volume for NFL
  const applyLever = (newValue: number) => {
    if (nflLever) {
      const baseVolumes = hasCustomizations ? volumes : baseline.volumes;
      dispatch(
        setPlayerCustomization({
          playerId: player.id,
          customization: {
            gameId: game.id,
            eventStartTime: game.startTime,
            minutes: 0,
            rates: hasCustomizations ? rates : getBaselineRates(player),
            volumes: { ...baseVolumes, [nflLever.key]: newValue },
          },
        })
      );
      return;
    }
    dispatch(
      setPlayerCustomization({
        playerId: player.id,
        customization: {
          gameId: game.id,
          eventStartTime: game.startTime,
          minutes: newValue,
          rates: hasCustomizations ? rates : getBaselineRates(player),
        },
      })
    );
  };

  // Track previous blend weights to detect changes
  const prevBlendRef = useRef({ stokWeight: filters.stokWeight, marketWeight: filters.marketWeight });

  // Trigger EV calculation when:
  // 1. Player has customizations (projections changed)
  // 2. Blend weights change (need to recalculate EV for all players)
  // Note: triggerEVForAllStats is intentionally excluded from deps to prevent infinite loops
  useEffect(() => {
    const blendChanged =
      prevBlendRef.current.stokWeight !== filters.stokWeight ||
      prevBlendRef.current.marketWeight !== filters.marketWeight;

    // Update ref for next comparison
    prevBlendRef.current = { stokWeight: filters.stokWeight, marketWeight: filters.marketWeight };

    // Trigger recalculation if player has customizations OR blend changed
    if (hasCustomizations || blendChanged) {
      triggerEVForAllStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCustomizations, projectionsKey, filters.stokWeight, filters.marketWeight]);

  // Update betslip projections when player customizations change (every market the player has, any league),
  // including the reset back to baseline — otherwise slip entries would keep the last customized value.
  const hadCustomizationsRef = useRef(false);
  useEffect(() => {
    const transitionedToBaseline = hadCustomizationsRef.current && !hasCustomizations;
    hadCustomizationsRef.current = hasCustomizations;
    if (hasCustomizations || transitionedToBaseline) {
      const projectionsByStat: Record<string, string> = {};
      for (const prop of player.props || []) {
        const key = prop.stat.toLowerCase().replace(/[\s\-_]/g, '');
        projectionsByStat[key] = projectionForStat(projections, filters.league, prop.stat).toFixed(getStatDecimals(prop.stat, filters.league));
      }
      dispatch(
        updateBetSlipProjections({
          playerId: player.id,
          projections: {
            pts: projections.pts,
            reb: projections.reb,
            ast: projections.ast,
            threes: projections.threes,
            pra: projections.pts + projections.reb + projections.ast,
          },
          projectionsByStat,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, player.id, player.props, hasCustomizations, projectionsKey, filters.league]);

  // Handle lever change via stepper (delta is in steps)
  const handleMinutesChange = (delta: number) => {
    const next = Math.max(0, Math.min(leverMax, leverValue + delta * leverStep));
    applyLever(parseFloat(next.toFixed(1)));
  };

  // Direct minutes input change - only updates local state while typing
  // Restricts input to one decimal place
  const handleMinutesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Allow empty, or valid number format with at most one decimal place
    if (rawValue === '' || /^\d*\.?\d?$/.test(rawValue)) {
      setMinutesInputValue(rawValue);
    }
  };

  // On blur, validate and dispatch to Redux
  const handleMinutesInputBlur = () => {
    const parsed = parseFloat(minutesInputValue);
    if (isNaN(parsed) || minutesInputValue === '') {
      // Invalid input - reset to current lever value
      setMinutesInputValue(leverValue.toFixed(1));
    } else {
      // Valid input - clamp, format, and dispatch to Redux
      const clamped = Math.max(0, Math.min(leverMax, parsed));
      setMinutesInputValue(clamped.toFixed(1));
      applyLever(clamped);
    }
  };

  // Check if the lever has been modified from baseline
  const isMinutesModified = Math.abs(leverValue - leverBaseline) > 0.1;

  // Reset minutes to baseline and cancel custom EVs
  const handleResetMinutes = () => {
    dispatch(resetPlayerCustomization(player.id));
    cancelEVCalculation();
  };

  // Check if current league is NBA (for showing/hiding rate editing features)
  const isNBA = filters.league === 'NBA';
  const isNHL = filters.league === 'NHL';
  // Editable (minutes/volume × rates) card: NBA, NHL, and NFL players with a volume lever
  const canEditRates = isNBA || isNHL || nflLever !== null;
  // MLB and NFL: stat columns are as many as the card can fit, with a +N overflow chip for the rest
  const usesDynamicStats = leagueUsesDynamicStats(filters.league);

  // Measure card width to dynamically cap dynamic stats
  const cardRef = useRef<HTMLDivElement>(null);
  const [desktopStatCap, setDesktopStatCap] = useState(20);

  useEffect(() => {
    if (!cardRef.current || !usesDynamicStats) return;
    const GAP = 24;
    const STAT_COL_W = (isNFL ? 92 : 70) + GAP;
    // player info + card padding + caret area + buffer, plus NFL's Edit Rates (90) and lever (110) columns
    const FIXED_W = 280 + 32 + 52 + 32 + (isNFL ? 90 + 110 + 2 * GAP : 0);
    const calcCap = (width: number) => Math.max(2, Math.floor((width - FIXED_W) / STAT_COL_W));

    setDesktopStatCap(calcCap(cardRef.current.offsetWidth));

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newCap = calcCap(entry.contentRect.width);
        setDesktopStatCap((prev) => prev !== newCap ? newCap : prev);
      }
    });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [usesDynamicStats, isNFL]);

  // For MLB/NFL: build stats dynamically from the player's actual props, sorted by best EV
  // Capped downstream: mobile shows 4, desktop caps based on card width
  const dynamicStats = useMemo(() => {
    if (!usesDynamicStats || !player.props?.length) return [];

    // Dedupe by stat — keep the one with the best EV sportsbook
    const statMap = new Map<string, { key: string; label: string; value: number }>();
    for (const prop of player.props) {
      if (!statMap.has(prop.stat) && prop.projection > 0) {
        statMap.set(prop.stat, {
          key: prop.stat.toLowerCase(),
          label: prop.stat,
          value: prop.projection,
        });
      }
    }

    // Sort by best EV descending
    return Array.from(statMap.values())
      .sort((a, b) => {
        const evA = getOriginalBestEV(player, a.key) ?? -Infinity;
        const evB = getOriginalBestEV(player, b.key) ?? -Infinity;
        return evB - evA;
      });
  }, [usesDynamicStats, player]);

  // NFL card stats by position group. Same geometry as the NBA card: the lever is the
  // stepper, then EXACTLY four fixed columns — never filtered by what the feed carries —
  // so every card in the list lines up column-for-column (a stat the feed lacks reads
  // 0.0 / "-- EV", exactly like 3PM on an NBA center). The full market list lives in the
  // expanded table.
  // Ordered most-relevant first; the width cap decides how many fit and the rest roll into +N.
  // The lever (pass att / rush att / targets) is not repeated here — it has its own stepper.
  const nflStatDefs: Record<NflGroup, Array<{ key: keyof typeof projections; label: string }>> = {
    QB: [
      { key: 'passYds', label: 'PASS YDS' },
      { key: 'passTds', label: 'PASS TD' },
      { key: 'comp', label: 'COMP' },
      { key: 'ints', label: 'INT' },
      { key: 'rushYds', label: 'RUSH YDS' },
      { key: 'rushTds', label: 'RUSH TD' },
      { key: 'passRushYds', label: 'PASS+RUSH YDS' },
      { key: 'anyTd', label: 'ANY TD' },
    ],
    RB: [
      { key: 'rushYds', label: 'RUSH YDS' },
      { key: 'anyTd', label: 'ANY TD' },
      { key: 'receptions', label: 'REC' },
      { key: 'recYds', label: 'REC YDS' },
      { key: 'rushRecYds', label: 'RUSH+REC YDS' },
      { key: 'rushTds', label: 'RUSH TD' },
      { key: 'recTds', label: 'REC TD' },
      { key: 'rushRecTds', label: 'RUSH+REC TD' },
    ],
    REC: [
      { key: 'recYds', label: 'REC YDS' },
      { key: 'receptions', label: 'REC' },
      { key: 'anyTd', label: 'ANY TD' },
      { key: 'recTds', label: 'REC TD' },
      { key: 'rushRecYds', label: 'RUSH+REC YDS' },
      { key: 'rushYds', label: 'RUSH YDS' },
      { key: 'rushRecTds', label: 'RUSH+REC TD' },
    ],
  };
  const nflStats = nflGroup && nflLever
    ? [
        { key: nflLever.key as string, label: nflLever.label, value: leverValue, change: changes[nflLever.key] },
        ...nflStatDefs[nflGroup]
          // Keep a column only where there is something to say: a projection, or a line to bet against
          .filter((d) => (projections[d.key] ?? 0) > 0 || getBestEV(player, d.key as string, customEVData, hasCustomEV) !== null)
          .map((d) => ({ key: d.key as string, label: d.label, value: projections[d.key], change: changes[d.key] })),
      ]
    : [];

  // Stats config for mobile (with percentage changes) and desktop (with bestEV)
  // Use league-specific stats
  // For NHL, include minutes as display-only (not editable)
  const stats = isNBA
    ? [
        { key: 'mins', label: 'MINS', value: minutes, change: changes.minutes },
        { key: 'pts', label: 'PTS', value: projections.pts, change: changes.pts },
        { key: 'reb', label: 'REB', value: projections.reb, change: changes.reb },
        { key: 'ast', label: 'AST', value: projections.ast, change: changes.ast },
        { key: 'threes', label: '3PM', value: projections.threes, change: changes.threes },
      ]
    : isNHL
      ? [
          { key: 'mins', label: 'MINS', value: minutes, change: changes.minutes },
          { key: 'goals', label: 'G', value: projections.goals, change: changes.goals },
          { key: 'nhlAssists', label: 'A', value: projections.nhlAssists, change: changes.nhlAssists },
          { key: 'sog', label: 'SOG', value: projections.sog, change: changes.sog },
          { key: 'blocks', label: 'BLK', value: projections.blocks, change: changes.blocks },
        ]
      : nflGroup
        ? nflStats
        : usesDynamicStats
          ? dynamicStats.map((s) => ({ ...s, change: undefined }))
          : [];

  // Mobile: cap at 4 stats since screen is narrow
  const MOBILE_STAT_CAP = 4;
  const mobileStats = usesDynamicStats ? stats.slice(0, MOBILE_STAT_CAP) : stats;
  const mobileOverflow = usesDynamicStats ? Math.max(0, stats.length - MOBILE_STAT_CAP) : 0;

  // Mobile layout
  if (isMobile) {
    return (
      <Paper
        sx={{
          backgroundColor: '#161617',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Main Card Content - Clickable to expand */}
        <Box
          onClick={handleCardClick}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            gap: 1.5,
            cursor: 'pointer',
          }}
        >
          {/* Row 1: Avatar, Name/Position/Team, Edit Rates, Caret */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={player.avatar}
              alt={player.name}
              sx={{ width: 32, height: 32, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  color="text.primary"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {player.name}
                </Typography>
                {player.battingOrder !== undefined && (
                  <BattingOrderBadge battingOrder={player.battingOrder} confirmedLineup={player.confirmedLineup} />
                )}
                {(() => {
                  const injuryDisplay = getInjuryDisplay(player.injuryStatus);
                  if (!injuryDisplay) return null;
                  return (
                    <Box
                      sx={{
                        backgroundColor: injuryDisplay.bgColor,
                        color: injuryDisplay.color,
                        fontSize: '10px',
                        fontWeight: 700,
                        fontFamily: '"Instrument Sans", sans-serif',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: '4px',
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      {injuryDisplay.text}
                    </Box>
                  );
                })()}
              </Box>
              <Typography color="text.secondary" sx={{ fontSize: '13.33px', lineHeight: 1.5 }}>
                {player.position ? `${player.position}${player.hand ? ` (${player.hand})` : ''} - ` : ''}{player.team}
              </Typography>
            </Box>
            {canEditRates && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditRatesOpen(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'text.primary',
                    fontSize: '11.11px',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1,
                    minWidth: 'auto',
                    borderRadius: '5.555px',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Edit Rates
                </Button>
                {hasCustomizations && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetMinutes();
                    }}
                    sx={{
                      color: 'text.secondary',
                      p: 0.5,
                      '&:hover': {
                        color: 'text.primary',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ArrowClockwise size={16} weight="regular" />
                  </IconButton>
                )}
              </Box>
            )}
            <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
              {isExpanded ? (
                <CaretUp size={20} weight="bold" />
              ) : (
                <CaretDown size={20} weight="bold" />
              )}
            </Box>
          </Box>

          {/* Row 2: Stats Row - MINS, PTS, REB, AST, 3PM */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {mobileStats.map((stat) => {
              // Get EV% for non-MINS stats (always show EV, not percentage changes)
              const bestEV = stat.key !== leverKey ? getBestEV(player, stat.key, customEVData, hasCustomEV) : null;
              const evDisplay = formatEV(bestEV);

              // For MINS, show percentage change if modified
              const isMinutesWithChange = stat.key === leverKey && stat.change !== undefined && Math.abs(stat.change) >= 0.1;
              const changeColor = stat.change && stat.change > 0 ? '#66BB6A' : stat.change && stat.change < 0 ? '#EF5350' : 'text.secondary';
              const changeSign = stat.change && stat.change > 0 ? '+' : '';

              return (
                <Box key={stat.key} sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: '13.33px', lineHeight: 1.5 }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    color={stat.key === leverKey ? 'text.secondary' : 'text.primary'}
                    sx={{
                      fontWeight: stat.key === leverKey ? 400 : 600,
                      fontSize: '16px',
                      lineHeight: 1.5,
                    }}
                  >
                    {stat.value.toFixed(getStatDecimals(stat.label, filters.league))}
                  </Typography>
                  {stat.key === leverKey ? (
                    // For MINS: show percentage change if modified, otherwise empty placeholder
                    isMinutesWithChange ? (
                      <Typography
                        sx={{ color: changeColor, fontSize: '11.11px', lineHeight: 1.5 }}
                      >
                        {changeSign}{stat.change?.toFixed(1)}%
                      </Typography>
                    ) : (
                      <Typography
                        sx={{ color: 'transparent', fontSize: '11.11px', lineHeight: 1.5 }}
                      >
                        -
                      </Typography>
                    )
                  ) : (
                    <Typography
                      sx={{ color: evDisplay.text ? evDisplay.color : 'rgba(242, 242, 243, 0.2)', fontSize: '11.11px', lineHeight: 1.5 }}
                    >
                      {evDisplay.text || '-- EV'}
                    </Typography>
                  )}
                </Box>
              );
            })}
            {mobileOverflow > 0 && (
              <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', pl: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontSize: '13.33px',
                    fontWeight: 600,
                    color: '#42A5F5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  +{mobileOverflow}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Expanded Stats Table - only render when expanded to avoid layout thrashing */}
        {isExpanded && (
          <Box
            sx={{
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              p: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            }}
          >
            <StatsTable
              player={player}
              game={game}
              customProjections={projections}
              customEVData={customEVData}
              isCustomEV={hasCustomEV}
              isEVLoading={isEVLoading}
            />
          </Box>
        )}

        {/* Edit Rates Dialog */}
        <EditRatesDialog
          open={editRatesOpen}
          onClose={() => setEditRatesOpen(false)}
          player={player}
          gameId={game.id}
          eventStartTime={game.startTime}
          currentMinutes={minutes}
          currentRates={rates}
          baselineMinutes={baseline.minutes}
          baselineRates={baseline.rates}
          currentVolumes={volumes}
          baselineVolumes={baseline.volumes}
          league={filters.league}
          onApplyWithStats={triggerEVForStats}
          onReset={cancelEVCalculation}
        />
      </Paper>
    );
  }

  // Desktop stats (exclude the lever — MINS or NFL volume — as it has its own stepper)
  const allDesktopStats = canEditRates ? stats.slice(1) : stats;
  // NFL players without a lever (kickers, TD-only backups) still sit in the NFL grid: the
  // Edit Rates + stepper slots are reserved (empty) and their feed stats fill the same four
  // column slots, padded so the columns stay put.
  const nflGrid = isNFL;
  const desktopStats = usesDynamicStats ? allDesktopStats.slice(0, desktopStatCap) : allDesktopStats;
  const desktopOverflow = usesDynamicStats ? Math.max(0, allDesktopStats.length - desktopStatCap) : 0;
  // Columns pack left, so nothing needs padding on the right; players with no lever keep their leading
  // Edit Rates + stepper slots (reserveEditorSlots) so their stats start at the same x as everyone else.
  const reserveEditorSlots = nflGrid && !canEditRates;
  // NFL labels run longer (RUSH+REC YDS); the row is space-between so the extra width is free
  const statColW = nflGrid ? 92 : 70;

  return (
    <Paper
      ref={cardRef}
      sx={{
        backgroundColor: '#161617',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Main Card Content - Clickable to expand */}
      <Box
        onClick={handleCardClick}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          p: 2,
          gap: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
          },
        }}
      >
        {/* Player Info - Fixed width for column alignment */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: 280, flexShrink: 0 }}>
          <Avatar
            src={player.avatar}
            alt={player.name}
            sx={{ width: 48, height: 48, flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle1"
                color="text.primary"
                sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {player.name}
              </Typography>
              {player.battingOrder !== undefined && (
                <BattingOrderBadge battingOrder={player.battingOrder} confirmedLineup={player.confirmedLineup} />
              )}
              {(() => {
                const injuryDisplay = getInjuryDisplay(player.injuryStatus);
                if (!injuryDisplay) return null;
                return (
                  <Box
                    sx={{
                      backgroundColor: injuryDisplay.bgColor,
                      color: injuryDisplay.color,
                      fontSize: '10px',
                      fontWeight: 700,
                      fontFamily: '"Instrument Sans", sans-serif',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {injuryDisplay.text}
                  </Box>
                );
              })()}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {player.position ? `${player.position}${player.hand ? ` (${player.hand})` : ''} - ` : ''}{player.team}
            </Typography>
          </Box>
        </Box>

        {/* Stats Row - Edit Rates + MINS + PTS/REB/AST/3PM with space-between */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            // NFL and MLB pack left on a fixed grid so columns line up across every card; NBA/NHL keep
            // their original spread, which works because their stat count never varies.
            justifyContent: canEditRates && !nflGrid ? 'space-between' : 'flex-start',
            alignItems: 'center',
            gap: canEditRates && !nflGrid ? 0 : 3,
          }}
        >
          {/* NFL, no lever: hold the Edit Rates + stepper slots so the stat columns align */}
          {reserveEditorSlots && (
            <>
              <Box sx={{ width: 90, flexShrink: 0 }} />
              <Box sx={{ width: 110, flexShrink: 0 }} />
            </>
          )}

          {/* Edit Rates Button Column */}
          {canEditRates && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 90,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditRatesOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'text.primary',
                  fontSize: '13px',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Edit Rates
              </Button>
            </Box>
          )}

          {/* Lever Stepper Column (minutes, or NFL volume) */}
          {canEditRates && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 110,
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                {leverLabel}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: isMinutesModified
                      ? 'rgba(66, 165, 245, 0.1)'
                      : 'rgba(255, 255, 255, 0.05)',
                    border: isMinutesModified
                      ? '1px solid #42A5F5'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMinutesChange(-1);
                    }}
                    sx={{ color: 'text.secondary', borderRadius: 0 }}
                  >
                    <CaretDown size={16} weight="bold" />
                  </IconButton>
                  <Box
                    component="input"
                    type="text"
                    inputMode="decimal"
                    value={minutesInputValue}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    onChange={handleMinutesInputChange}
                    onBlur={handleMinutesInputBlur}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    sx={{
                      width: 50,
                      textAlign: 'center',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 500,
                      outline: 'none',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMinutesChange(1);
                    }}
                    sx={{ color: 'text.secondary', borderRadius: 0 }}
                  >
                    <CaretUp size={16} weight="bold" />
                  </IconButton>
                </Box>
                {/* Reset icon - only show when customized, positioned absolutely */}
                {hasCustomizations && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetMinutes();
                    }}
                    sx={{
                      position: 'absolute',
                      right: -24,
                      color: 'text.secondary',
                      p: 0.5,
                      '&:hover': {
                        color: 'text.primary',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ArrowClockwise size={16} weight="regular" />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {/* Stat Columns */}
          {desktopStats.map((stat) => {
            const evDisplay = formatEV(getBestEV(player, stat.key, customEVData, hasCustomEV));
            return (
              <Box key={stat.key} sx={{ textAlign: 'center', width: statColW, flexShrink: usesDynamicStats && !nflGrid ? 1 : 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {stat.label}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.primary"
                  sx={{ fontWeight: 600, fontSize: '18px' }}
                >
                  {stat.value.toFixed(getStatDecimals(stat.label, filters.league))}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: evDisplay.text ? evDisplay.color : 'rgba(242, 242, 243, 0.2)', fontSize: '11px' }}
                >
                  {evDisplay.text || '-- EV'}
                </Typography>
              </Box>
            );
          })}
          {desktopOverflow > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pl: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: '#42A5F5',
                  whiteSpace: 'nowrap',
                }}
              >
                +{desktopOverflow}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Expand/Collapse Indicator */}
        <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', px: 1 }}>
          {isExpanded ? (
            <CaretUp size={20} weight="bold" />
          ) : (
            <CaretDown size={20} weight="bold" />
          )}
        </Box>
      </Box>

      {/* Expanded Stats Table - only render when expanded to avoid layout thrashing */}
      {isExpanded && (
        <Box
          sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          }}
        >
          <StatsTable
            player={player}
            game={game}
            customProjections={projections}
            customEVData={customEVData}
            isCustomEV={hasCustomEV}
            isEVLoading={isEVLoading}
          />
        </Box>
      )}

      {/* Edit Rates Dialog */}
      <EditRatesDialog
        open={editRatesOpen}
        onClose={() => setEditRatesOpen(false)}
        player={player}
        gameId={game.id}
        eventStartTime={game.startTime}
        currentMinutes={minutes}
        currentRates={rates}
        baselineMinutes={baseline.minutes}
        baselineRates={baseline.rates}
        currentVolumes={volumes}
        baselineVolumes={baseline.volumes}
        league={filters.league}
        onApplyWithStats={triggerEVForStats}
        onReset={cancelEVCalculation}
      />
    </Paper>
  );
};

export default memo(PlayerCard);
